import { Button, Layout, PageHeader } from 'antd';
import { useCallback, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { CurrencyWrapper } from 'app/components/commons/Currency/CurrencyWrapper/CurrencyWrapper';
import { DataLoadingSpinner } from 'app/components/commons/DataLoadingSpinner/DataLoadingSpinner';
import { useGiftCardDetails } from 'app/hooks/data/giftCards/useGetGiftCards';
import {
  useRefundGiftCard,
  useSaveGiftCard,
} from 'app/hooks/data/giftCards/useGiftCard';
import type {
  GiftCardDetails,
  GiftCardEditForm,
  RefundForm,
} from 'app/typings/giftCard';

import 'styles/pages/Booking/booking-details.scss';
import 'styles/pages/GiftCards/gift-card-details.scss';

import { BookingsLinked } from './BookingsLinked';
import { GiftCardEdit } from './GiftCardEdit/GiftCardEdit';
import { Payment } from './Payment/Payment';
import { Properties } from './Properties/Properties';
import { RefundModal } from './RefundModal/RefundModal';
import { Refunds } from './Refunds/Refunds';
import { Sender } from './Sender/Sender';
import { Summary } from './Summary/Summary';

export const GiftCardDetailsPage = () => {
  const { id: giftCardId } = useParams<{ id: string }>();
  const { data: giftCardDetails, isLoading } = useGiftCardDetails(giftCardId);

  if (!giftCardDetails || isLoading) {
    return <DataLoadingSpinner />;
  }

  return <GiftCardDetailsHydratedPage giftCardDetails={giftCardDetails} />;
};

type GiftCardDetailsHydratedProps = {
  giftCardDetails: GiftCardDetails;
};

export const GiftCardDetailsHydratedPage = ({
  giftCardDetails,
}: GiftCardDetailsHydratedProps) => {
  const [isRefundModalVisible, setIsRefundModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  const { mutateAsync: saveGiftCard } = useSaveGiftCard();
  const { mutateAsync: refundGiftCard } = useRefundGiftCard();

  const history = useHistory();

  const back = useCallback(() => {
    history.goBack();
  }, [history]);

  const hideModal = useCallback(() => {
    setIsEditModalVisible(false);
  }, []);

  const displayModal = useCallback(() => {
    setIsEditModalVisible(true);
  }, []);

  const onSubmit = useCallback(
    (body: GiftCardEditForm) => {
      saveGiftCard({ code: giftCardDetails.giftCard.code, body });

      setIsEditModalVisible(false);
    },
    [giftCardDetails.giftCard.code, saveGiftCard]
  );

  const editGiftCardButton = (
    <div className="giftcard-edit-btn">
      <Button size="large" onClick={displayModal}>
        Edit
      </Button>
    </div>
  );

  const onRefund = async (refundValues: RefundForm) => {
    await refundGiftCard({
      code: giftCardDetails.giftCard.code,
      body: refundValues,
    });
  };

  const { giftCard, giftCardBookings, refunds } = giftCardDetails;
  const { currency, code, countryId } = giftCard;

  return (
    <Layout className="booking-details">
      <CurrencyWrapper currency={currency} countryId={countryId}>
        <PageHeader
          className="header"
          onBack={back}
          title={`Gift card ${code}`}
          extra={editGiftCardButton}
        />
        <div className="body">
          <div className="left-part">
            {giftCardDetails.user && (
              <Sender giftCardDetails={giftCardDetails} />
            )}
            <BookingsLinked currency={currency} code={code} />
          </div>
          <div className="right-part">
            <Summary giftCard={giftCard} giftCardBookings={giftCardBookings} />
            <Properties giftCard={giftCard} />
            <Payment
              giftCard={giftCard}
              setIsRefundModalVisible={setIsRefundModalVisible}
            />
            <Refunds refunds={refunds} />
          </div>
        </div>
        <GiftCardEdit
          visible={isEditModalVisible}
          giftCard={giftCard}
          onCancel={hideModal}
          onSubmit={onSubmit}
        />
        {isRefundModalVisible && (
          <RefundModal
            giftCard={giftCard}
            refunds={refunds}
            title="Refund Gift Card"
            onOk={onRefund}
            onCancel={() => setIsRefundModalVisible(false)}
          />
        )}
      </CurrencyWrapper>
    </Layout>
  );
};
